import { useEffect, useState } from "react";

declare global {
    interface Window {
        UC_UI?: {
            showSecondLayer: () => Promise<void>;
            showFirstLayer: () => Promise<void>;
            getServicesBaseInfo: () => Promise<
                Array<{
                    id: string;
                    name: string;
                    categorySlug: string;
                    consent: {
                        status: boolean;
                    };
                }>
            >;
        };
    }
}

/**
 * Hook to check if the user has given consent for marketing cookies.
 * Uses Usercentrics UC_UI API.
 * Returns true if consent has been given, false otherwise.
 */
export const useMarketingConsent = () => {
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const checkMarketingConsent = async () => {
        if (!window.UC_UI) {
            return false;
        }

        try {
            const services = await window.UC_UI.getServicesBaseInfo();
            // Check if ANY marketing service has consent
            const hasMarketingConsent = services
                .filter((s) => s.categorySlug === "marketing")
                .some((s) => s.consent.status === true);
            return hasMarketingConsent;
        } catch (error) {
            console.error("Error checking marketing consent:", error);
            return false;
        }
    };

    const handleConsentChange = async () => {
        const consent = await checkMarketingConsent();
        if (consent !== marketingConsent) {
            setMarketingConsent(consent);
        }
    };

    useEffect(() => {
        // Wait for UC_UI to be available and check consent
        const checkConsentWithRetry = async () => {
            let attempts = 0;
            const maxAttempts = 20; // 2 seconds max wait

            const check = async () => {
                if (window.UC_UI) {
                    const consent = await checkMarketingConsent();
                    setMarketingConsent(consent);
                    setIsLoading(false);
                } else if (attempts < maxAttempts) {
                    attempts++;
                    setTimeout(check, 100);
                } else {
                    // UC_UI not available after retries, assume no consent
                    setIsLoading(false);
                }
            };

            check();
        };

        checkConsentWithRetry();

        // Listen for Usercentrics consent updates via dataLayer
        const originalPush = window.dataLayer?.push;
        if (window.dataLayer && originalPush) {
            window.dataLayer.push = function (...args: unknown[]) {
                const result = originalPush.apply(window.dataLayer, args);
                // Check if it's a consent_status event
                const firstArg = args[0] as { event?: string };
                if (firstArg?.event === "consent_status") {
                    handleConsentChange();
                }
                return result;
            };
        }

        return () => {
            // Restore original push
            if (window.dataLayer && originalPush) {
                window.dataLayer.push = originalPush;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { marketingConsent, isLoading };
};
